import { Link } from 'react-router-dom';

// Standards & Compliance page — the list of DPP standards Bloom's passports are
// built to align with, each with an honest "how we align" note. Reached from the
// global SiteFooter (App.js), which still renders beneath it, so no footer here.
//
// Wording is deliberate: we say "aligned with / built to," never "certified" or
// "compliant" — for a VOLUNTARY passport in a category with no delegated act, a
// legal-compliance claim isn't available to anyone. Keep it that way.
const EFFECTIVE_DATE = 'July 25, 2026';
const CONTACT_EMAIL = 'hello@love-bloom.com';

const Compliance = () => (
    <div className="legal-page">
        <div className="legal-container">
            <Link to="/" className="legal-home">← Back to Bloom</Link>
            <h1 className="legal-title">Standards &amp; Compliance</h1>
            <p className="legal-meta">Last updated: {EFFECTIVE_DATE}</p>

            <h2>Ecodesign for Sustainable Products Regulation — Regulation (EU) 2024/1781</h2>
            <p>
                The EU framework that establishes the Digital Product Passport and defines how
                product information must be structured, carried, and made accessible. We follow
                its DPP architecture voluntarily for categories that the regulation's current
                working plan does not yet cover.{' '}
                <a href="https://eur-lex.europa.eu/eli/reg/2024/1781/oj/eng" target="_blank" rel="noreferrer">Read the regulation</a>.
            </p>

            <h2>ESPR Articles 9 &amp; 10 — Passport data and access</h2>
            <p>
                These require a passport's data to be machine-readable, structured, interoperable,
                based on open standards, and reachable from the product's data carrier, with
                different information available to different parties. Bloom serves each passport
                both as a human-readable page and as a machine-readable JSON-LD record from the
                same QR code (content negotiation), and enforces tiered access — public product
                data for anyone, and restricted records visible only to authorised owners,
                brands, and their advisors.
            </p>

            <h2>ESPR Article 10(4) — Data persistence</h2>
            <p>
                Requires a passport to remain available even if the responsible operator ceases
                to exist. Each Bloom passport is issued as a portable, self-verifying record
                stamped with a content hash and, where enabled, a digital signature — so a copy
                can be archived, backed up, or handed on and still be checked for integrity
                independently of our servers.
            </p>

            <h2>CIRPASS-2 reference architecture</h2>
            <p>
                The EU-funded blueprint for the DPP data model and system. Our machine-readable
                passport is a JSON-LD graph rooted at the product's unique identifier, following
                this attribute model, with the consumer-facing presentation kept deliberately
                separate from the underlying data — as the architecture intends.
            </p>

            <h2>CEN-CENELEC JTC 24 — DPP standards</h2>
            <p>
                The joint technical committee developing the harmonised European standards for
                the DPP, including data authentication and integrity (prEN 18246). We align our
                data model and our integrity approach — a content hash with an optional
                cryptographic signature verifiable against a published key — to this emerging
                work. Several of these standards are still in draft.
            </p>

            <h2>ISO/IEC 18004 — QR Code</h2>
            <p>
                The international standard for QR-code symbology. Each piece's data carrier is a
                standard QR code that encodes the resolvable identifier URL for its passport.
            </p>

            <h2>GS1 Digital Link &amp; ISO/IEC 15459 — Product identifiers</h2>
            <p>
                The standards for globally unique product identifiers and their web-friendly
                encoding. Where a piece carries a GS1 Global Trade Item Number, Bloom emits a
                conformant GS1 Digital Link identifier; otherwise it uses an interim identifier
                until one is assigned.
            </p>

            <p>
                Questions may be sent to{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>
        </div>
    </div>
);

export default Compliance;
